import React, { useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../../../app/store/hooks/Redux'
import { BpTable } from '../../../../shared/components/cards/table/BpTable'
import { TTableResponse } from '../../../../shared/types/TableResponse.type'
import { ColumnsType } from 'antd/lib/table'
import { IBpPaginationProps } from '../../../../shared/components/cards/pagination/BpPagination'
import { AdvertiserReportFilterInterface } from '../../model/types/AdvertiserReportFilter.interface'
import {
  selectAdvertiserReportFilters,
  selectAdvertiserReportPagination,
  selectAllowDecloak,
  selectIsExpandAll,
  setAdvertiserReportPagination,
  setAllowDecloak,
  setTabCount,
} from '../../model/paidResultsSlice'
import {
  AdvertiserReportItem,
  AdvertiserReportsAllAdvertisersResponse,
  CommaLists,
  GetAdvertiserReportsAllAdvertisersApiArg,
  useGetAdvertiserReportsAllAdvertisersQuery,
} from '../../../../app/api'
import { IconButton } from '../../../../shared/components/buttons/icon-button/IconButton'
import { TriangleButton } from '../../../../shared/components/buttons/triangle/TriangleButton'
import { SorterResult } from 'antd/lib/table/interface'
import { useSorter } from '../../../../shared/lib/hooks/UseSorter'
import { PaidResultsTableExpandRow } from '../paid-results-table-expand-row/PaidResultsTableExpandRow'
import { IWithExpandedAllProps, withExpanded } from '../../../../shared/lib/hoc/WithExpanded'
import { BpTooltip } from '../../../../shared/components/cards/tooltip/BpTooltip'
import { IPagination } from '../../../../shared/types/Pagination.interface'
import { useTranslation } from 'react-i18next'
import CountryList from '../../../../shared/ui/cells/CountriesCell'
import { DomainCell } from '../../ui/cells/DomainCell'
import { VisibilityCell } from '../../ui/cells/VisibilityCell'
import { ActionsCell } from '../../ui/cells/ActionsCell'
import clsx from 'clsx'
import styles from './PaidResultsTable.module.scss'
import { AdvertiserCell } from '../../ui/cells/AdvertiserCell'
import { TabAllStub } from '../paid-results-stubs/TabAllStub'
import { TabCouponSitesStub } from '../paid-results-stubs/TabCouponSitesStub'
import { TabLookalikeDomains } from '../paid-results-stubs/TabLookalikeDomains'
import { TabBrandMentionsStub } from '../paid-results-stubs/TabBrandMentionsStub'
import { TabAffiliatesStub } from '../paid-results-stubs/TabAffiliatesStub'

const Table = ({
  onToggleExpand,
  isExpandAll,
  expandedRowKeys,
  onToggleExpandAll,
}: IWithExpandedAllProps) => {
  const { t } = useTranslation()
  const advertiserFilter = useAppSelector<AdvertiserReportFilterInterface>(
    selectAdvertiserReportFilters,
  )
  const advertiserPagination = useAppSelector<IPagination>(selectAdvertiserReportPagination)
  const allowDecloak = useAppSelector<boolean>(selectAllowDecloak)
  const isExpand = useAppSelector(selectIsExpandAll)
  const {
    project,
    dateRange: date,
    country,
    policy,
    advertiser,
    keyword,
    affiliate,
    advertiserName,
    show_hidden,
    show_shopping_ads,
    engine,
    browser,
    is_decloak,
    tags,
    reversed_filters,
  } = advertiserFilter
  const { current, pageSize, total } = advertiserPagination

  const [queryParams, setQueryParams] = useState<
    Partial<CommaLists<GetAdvertiserReportsAllAdvertisersApiArg>>
  >({
    page: current,
    'per-page': pageSize,
  })
  const {
    data: response,
    isLoading,
    isFetching,
  } = useGetAdvertiserReportsAllAdvertisersQuery(
    queryParams as GetAdvertiserReportsAllAdvertisersApiArg,
    {
      refetchOnMountOrArgChange: tags?.length,
      skip: !queryParams.projectId,
    },
  )
  const data = response as AdvertiserReportsAllAdvertisersResponse | undefined
  const dispatch = useAppDispatch()
  const [rowTabs, setRowTabs] = useState<
    Record<string, 'latest_ads' | 'affiliates' | 'advertisers'>
  >({})

  const handleShowAdvertisers = (advertiser: string) => {
    setRowTabs((prev) => ({ ...prev, [advertiser]: 'advertisers' }))

    const isExpanded = isExpandAll || !!expandedRowKeys?.includes(advertiser)
    if (!isExpanded) onToggleExpand?.(true, advertiser)
  }

  useEffect(() => {
    if (!project) return

    setQueryParams((prev) => ({
      ...prev,
      projectId: +project,
      countryId: country || undefined,
      engineId: engine || undefined,
      browserId: browser || undefined,
      policy: policy as GetAdvertiserReportsAllAdvertisersApiArg['policy'],
      date,
      site: advertiser,
      keywordId: keyword || undefined,
      affiliateId: affiliate || undefined,
      advertiserId: advertiserName || undefined,
      showHidden: show_hidden,
      showShoppingAds: show_shopping_ads,
      isDecloak: is_decloak,
      tagIds: tags || undefined,
      reversedFilters: (reversed_filters || undefined) as unknown as string[],
    }))
  }, [advertiserFilter])

  useEffect(() => {
    if (data?.data?.tab_count !== undefined) {
      dispatch(setTabCount(data.data.tab_count))
    }

    if (data?.data?.allow_decloak !== undefined) {
      dispatch(setAllowDecloak(data.data.allow_decloak as boolean))
    }
  }, [data])

  useEffect(() => {
    if (onToggleExpandAll) {
      onToggleExpandAll(isExpand)
    }
  }, [isExpand])

  const columns: ColumnsType<AdvertiserReportItem> = [
    {
      title: t('table.columns.domain'),
      dataIndex: 'advertiser',
      key: 'advertiser',
      render: (_: string, record) => <DomainCell {...record} project={project} />,
      width: '280px',
    },
    {
      title: t('table.columns.advertiser'),
      dataIndex: 'advertisers',
      key: 'advertisers',
      render: (_: string, record) => (
        <AdvertiserCell
          {...record}
          onShowAdvertisers={() => handleShowAdvertisers(record.advertiser)}
        />
      ),
      width: '230px',
    },
    {
      title: t('table.columns.adsAnalyzed'),
      showSorterTooltip: false,
      dataIndex: 'count_total',
      key: 'count_total',
      width: '150px',
      sorter: true,
    },
    {
      title: t('table.columns.keywords'),
      dataIndex: 'keywords_count',
      key: 'keywords_count',
      sorter: true,
    },
    {
      title: () => (
        <span className={clsx(styles.columnTitle)}>
          {t('table.columns.visibility')}{' '}
          <BpTooltip title={t('pages.paidResults.visibilityTooltip')} />
        </span>
      ),
      dataIndex: 'visibility',
      key: 'visibility',
      render: (_: number, record) => <VisibilityCell {...record} />,
      sorter: true,
    },
    {
      title: t('table.columns.affiliates'),
      dataIndex: 'affiliates_count',
      key: 'affiliates_count',
      sorter: true,
    },
    {
      title: t('table.columns.countries'),
      dataIndex: 'countries',
      key: 'countries',
      render: (_: string, { countries }: AdvertiserReportItem) => (
        <CountryList countries={countries} max={3} />
      ),
    },
    {
      title: t('table.columns.lastFound'),
      key: 'last_found',
      render: (_: AdvertiserReportItem, record) => (
        <ActionsCell {...record} allowDecloak={allowDecloak} project={project} />
      ),
      dataIndex: 'last_found',
      sorter: true,
      width: 150,
    },
  ]

  const handleTableChange = (
    { pageSize, current, total }: IBpPaginationProps,
    sorter?: SorterResult<AdvertiserReportItem> | SorterResult<AdvertiserReportItem>[],
  ) => {
    dispatch(
      setAdvertiserReportPagination({
        pageSize: pageSize as number,
        current: current as number,
        total: total as number,
      }),
    )
    setQueryParams({
      ...queryParams,
      page: current,
      'per-page': pageSize,
      sort: useSorter(sorter),
    })
  }

  return (
    <section className={clsx(styles.root)}>
      <BpTable
        stickyExpandedRow
        emptyStub={
          {
            default: <TabAllStub project={project} />,
            non_compliant_affiliates: <TabAffiliatesStub project={project} />,
            trademark_abuse: <TabBrandMentionsStub project={project} />,
            coupon_sites: <TabCouponSitesStub project={project} />,
            trademark_abuse_url: <TabLookalikeDomains project={project} />,
          }[policy || 'default']
        }
        expandable={{
          expandIcon: ({ expanded, onExpand, record }) => (
            <IconButton onClick={(e) => onExpand(record, e)}>
              <TriangleButton color='#A059FF' position={expanded ? 'top' : 'down'} />
            </IconButton>
          ),
          expandedRowRender: (record: AdvertiserReportItem) => (
            <PaidResultsTableExpandRow
              {...record}
              selectedTab={rowTabs[record.advertiser] ?? 'latest_ads'}
              onTabChange={(tab) =>
                setRowTabs((prev) => ({
                  ...prev,
                  [record.advertiser]: tab as 'latest_ads' | 'affiliates' | 'advertisers',
                }))
              }
            />
          ),
          onExpand: (expanded, { advertiser }) => {
            if (onToggleExpand) onToggleExpand(expanded, `${advertiser}`)
          },
          expandedRowKeys: isExpandAll
            ? data?.data?.items?.map((item) => item.advertiser)
            : expandedRowKeys,
        }}
        pagination={{
          current,
          pageSize,
          total,
        }}
        columns={columns}
        data={data as TTableResponse<AdvertiserReportItem>}
        dataSource={data?.data?.items}
        loading={isLoading || isFetching}
        rowClassName={(e) => (e.is_hide ? 'disabled' : '')}
        rowKey={(record) => `${record.advertiser}`}
        scroll={{ x: '1460px' }}
        onClickRow={(record) => {
          const isExpanded = isExpandAll ? true : expandedRowKeys?.includes(record.advertiser)

          onToggleExpand?.(!isExpanded, record.advertiser)
        }}
        onChange={(pagination, filters, sorter) => handleTableChange(pagination, sorter)}
      />
    </section>
  )
}

export const PaidResultsTable = withExpanded(Table)
